import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Select,
  Textarea,
  Card,
  LoadingScreen,
  Alert
} from '@azyntor-labs/nexus-ui';
import { ArrowLeft, Save, X } from 'lucide-react';
import { questionsApi } from '../../api/endpoints';
import { Question, QuestionType } from '../../types';

interface QuestionOption {
  id: string;
  option_text: string;
  is_correct: boolean;
}

interface QuestionFormData {
  question_text: string;
  question_type: QuestionType;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
  options: QuestionOption[];
  correct_answer: string;
  explanation: string;
}

export function QuestionEdit() {
  const { bankId, questionId } = useParams<{ bankId: string; questionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<QuestionFormData>({
    question_text: '',
    question_type: 'mcq',
    difficulty: 'medium',
    points: 1,
    options: [
      { id: crypto.randomUUID(), option_text: '', is_correct: false },
      { id: crypto.randomUUID(), option_text: '', is_correct: false },
      { id: crypto.randomUUID(), option_text: '', is_correct: false },
      { id: crypto.randomUUID(), option_text: '', is_correct: false },
    ],
    correct_answer: '',
    explanation: '',
  });

  // Fetch question data
  const { data: question, isLoading } = useQuery<Question>({
    queryKey: ['question', questionId],
    queryFn: async () => {
      const response = await questionsApi.get(questionId!);
      return response.data.data;
    },
    enabled: !!questionId,
  });

  // Populate form when question loads
  useEffect(() => {
    if (question) {
      console.log('[QuestionEdit] Loading question data:', question);
      console.log('[QuestionEdit] Question type:', question.question_type);
      console.log('[QuestionEdit] Correct answer:', question.correct_answer);

      // Map backend options (text property) to form options (option_text property)
      // Backend QuestionOption doesn't have is_correct; derive it from correct_answer array
      // correct_answer may contain option text OR option IDs
      const correctAnswers = Array.isArray(question.correct_answer) ? question.correct_answer : [];
      const mappedOptions = question.options && question.options.length > 0
        ? question.options.map(opt => ({
            id: opt.id || crypto.randomUUID(),
            option_text: opt.text || '',
            is_correct: correctAnswers.includes(opt.text) || correctAnswers.includes(opt.id),
          }))
        : [
            { id: crypto.randomUUID(), option_text: '', is_correct: false },
            { id: crypto.randomUUID(), option_text: '', is_correct: false },
            { id: crypto.randomUUID(), option_text: '', is_correct: false },
            { id: crypto.randomUUID(), option_text: '', is_correct: false },
          ];

      // Convert correct_answer from array to string and normalize to lowercase
      let correctAnswerStr = Array.isArray(question.correct_answer)
        ? question.correct_answer[0] || ''
        : question.correct_answer || '';

      // For true/false questions, normalize to lowercase
      if (question.question_type === 'true_false' && correctAnswerStr) {
        correctAnswerStr = correctAnswerStr.toLowerCase();
      }

      const newFormData = {
        question_text: question.question_text,
        question_type: question.question_type,
        difficulty: question.difficulty,
        points: question.points,
        options: mappedOptions,
        correct_answer: correctAnswerStr,
        explanation: question.explanation || '',
      };

      console.log('[QuestionEdit] Setting form data:', newFormData);
      console.log('[QuestionEdit] Normalized correct_answer:', correctAnswerStr);

      setFormData(newFormData);
    }
  }, [question]);

  // Update question mutation
  const updateQuestionMutation = useMutation({
    mutationFn: async (data: QuestionFormData) => {
      const payload: any = {
        question_text: data.question_text,
        question_type: data.question_type,
        difficulty: data.difficulty,
        points: Number(data.points),
        explanation: data.explanation || null,
      };

      if (data.question_type === 'mcq') {
        payload.options = data.options.map(opt => ({
          text: opt.option_text,
          is_correct: opt.is_correct,
        }));
        // Send correct_answer as text of correct options (backend stores this separately)
        payload.correct_answer = data.options
          .filter(opt => opt.is_correct)
          .map(opt => opt.option_text);
      } else if (data.question_type === 'true_false') {
        payload.correct_answer = data.correct_answer ? [data.correct_answer] : [];
      } else if (data.question_type === 'short_answer' || data.question_type === 'essay' || data.question_type === 'coding' || data.question_type === 'fill_blank') {
        payload.correct_answer = data.correct_answer ? [data.correct_answer] : [];
      }

      const response = await questionsApi.update(questionId!, payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', bankId] });
      queryClient.invalidateQueries({ queryKey: ['question', questionId] });
      navigate(`/teacher/question-banks/${bankId}/questions`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update question');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.question_type === 'mcq') {
      const hasCorrect = formData.options.some(opt => opt.is_correct);
      if (!hasCorrect) {
        setError('Please mark at least one option as correct');
        return;
      }
    }

    if (formData.question_type === 'true_false' && !formData.correct_answer) {
      setError('Please select the correct answer');
      return;
    }

    updateQuestionMutation.mutate(formData);
  };

  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { id: crypto.randomUUID(), option_text: '', is_correct: false }],
    });
  };

  const handleRemoveOption = (id: string) => {
    if (formData.options.length <= 2) return;
    setFormData({
      ...formData,
      options: formData.options.filter(opt => opt.id !== id),
    });
  };

  const handleOptionChange = (id: string, field: keyof QuestionOption, value: any) => {
    setFormData({
      ...formData,
      options: formData.options.map(opt =>
        opt.id === id ? { ...opt, [field]: value } : opt
      ),
    });
  };

  const questionTypeOptions = [
    { value: 'mcq', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True/False' },
    { value: 'fill_blank', label: 'Fill in the Blank' },
    { value: 'short_answer', label: 'Short Answer' },
    { value: 'essay', label: 'Essay' },
    { value: 'coding', label: 'Coding' },
  ];

  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];

  if (isLoading) {
    return <LoadingScreen message="Loading question..." variant="cyber" />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate(`/teacher/question-banks/${bankId}/questions`)}
            className="mb-2"
          >
            Back to Questions
          </Button>
          <h1 className="text-3xl font-bold text-white">Edit Question</h1>
          <p className="text-white/50 mt-1">Update question details and settings</p>
        </div>
      </div>

      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6">
          {/* Question Text */}
          <Textarea
            label="Question Text"
            required
            value={formData.question_text}
            onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
            placeholder="Enter your question"
            rows={4}
          />

          {/* Type, Difficulty, Points */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Question Type"
              required
              options={questionTypeOptions}
              value={formData.question_type}
              onChange={(e) => {
                const newType = e.target.value as QuestionType;
                setFormData({
                  ...formData,
                  question_type: newType,
                  options: newType === 'mcq' ? formData.options : [],
                  correct_answer: '',
                });
              }}
            />

            <Select
              label="Difficulty"
              required
              options={difficultyOptions}
              value={formData.difficulty}
              onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
            />

            <Input
              label="Points"
              type="number"
              required
              min={1}
              value={formData.points}
              onChange={(e) => setFormData({ ...formData, points: Number(e.target.value) })}
            />
          </div>

          {/* Multiple Choice Options */}
          {formData.question_type === 'mcq' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white">Options</label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddOption}
                >
                  Add Option
                </Button>
              </div>
              <div className="space-y-3">
                {formData.options.map((option, index) => (
                  <div key={option.id} className="flex items-center gap-3">
                    <Input
                      placeholder={`Option ${index + 1}`}
                      required
                      value={option.option_text}
                      onChange={(e) => handleOptionChange(option.id, 'option_text', e.target.value)}
                      className="flex-1"
                    />
                    <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={option.is_correct}
                        onChange={(e) => handleOptionChange(option.id, 'is_correct', e.target.checked)}
                        className="w-4 h-4"
                      />
                      Correct
                    </label>
                    {formData.options.length > 2 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveOption(option.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* True/False */}
          {formData.question_type === 'true_false' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Correct Answer</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-white cursor-pointer">
                  <input
                    type="radio"
                    name="correct_answer"
                    value="true"
                    checked={formData.correct_answer === 'true'}
                    onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                    className="w-4 h-4"
                  />
                  True
                </label>
                <label className="flex items-center gap-2 text-white cursor-pointer">
                  <input
                    type="radio"
                    name="correct_answer"
                    value="false"
                    checked={formData.correct_answer === 'false'}
                    onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                    className="w-4 h-4"
                  />
                  False
                </label>
              </div>
            </div>
          )}

          {/* Short Answer/Essay/Coding/Fill in the Blank */}
          {(formData.question_type === 'short_answer' ||
            formData.question_type === 'essay' ||
            formData.question_type === 'coding' ||
            formData.question_type === 'fill_blank') && (
            <Textarea
              label="Sample Answer (Optional)"
              value={formData.correct_answer}
              onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
              placeholder="Provide a sample answer or grading rubric"
              rows={3}
            />
          )}

          {/* Explanation */}
          <Textarea
            label="Explanation (Optional)"
            value={formData.explanation}
            onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
            placeholder="Provide an explanation for the correct answer"
            rows={3}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/teacher/question-banks/${bankId}/questions`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<Save className="w-4 h-4" />}
              disabled={updateQuestionMutation.isPending}
            >
              {updateQuestionMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
